import classNames from "classnames";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import {
  SanityRawContent,
  SanityTabs,
  SanityTabsLayout
} from "../../model/common";
import { useIsRtl } from "../../utils/LayoutContext";
import { useGatsbyLocation } from "../../utils/useGatsbyLocation";
import useLocation from "../../utils/useLocation";
import BlockContent, { Cap, CustomEmbeds } from "../BlockContent/BlockContent";
import PlainButton from "../Button/PlainButton/PlainButton";
import Card from "../Card/Card";
import Text from "../Text/Text";
import styles from "./Tabs.module.scss";
import { navigateWithUrlSearchParams } from "../../utils/navigate-with-params";

export interface TabsProps {
  node: SanityTabs;
  className?: string;
  customEmbeds?: CustomEmbeds;
  cap?: Cap;
}

const Tabs = ({
  node: { _key, content, layout, useTabButtons, queryParameter },
  className,
  customEmbeds,
  cap = "none"
}: TabsProps) => {
  // This must be a fixed initial value to avoid mismatch of server
  // and client component classNames.
  const [active, setActive] = useState(0);

  const { search } = useGatsbyLocation();
  const queryParams = new URLSearchParams(search);
  const queryParamValue = queryParameter
    ? queryParams.get(queryParameter)
    : null;

  const getActiveContent = useCallback(
    (queryParamValue: string | null): number => {
      const contentIndex = content.findIndex(
        tabContent => tabContent.queryParameterValue === queryParamValue
      );
      return contentIndex < 0 ? 0 : contentIndex;
    },
    [content]
  );

  useEffect(() => {
    setActive(getActiveContent(queryParamValue));
  }, [getActiveContent, queryParamValue]);

  const keyboardNavigation = useRef<boolean>(false);
  const isRtl = useIsRtl();
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      keyboardNavigation.current = false;
      const nextTab = () => {
        setActive(prevIndex => {
          if (prevIndex === content.length - 1) {
            return 0;
          } else {
            return ++prevIndex;
          }
        });
      };
      const prevTab = () => {
        setActive(prevIndex => {
          if (prevIndex === 0) {
            return content.length - 1;
          } else {
            return --prevIndex;
          }
        });
      };
      const firstTab = () => {
        setActive(0);
      };
      const lastTab = () => {
        setActive(content.length - 1);
      };

      const ArrowStart = isRtl ? "ArrowRight" : "ArrowLeft";
      const ArrowEnd = isRtl ? "ArrowLeft" : "ArrowRight";

      const keyFunctionMap: Record<string, () => void> = {
        [ArrowStart]: prevTab,
        [ArrowEnd]: nextTab,
        Home: firstTab,
        End: lastTab
      };

      const action = keyFunctionMap[event.key];

      if (action) {
        event.preventDefault();
        action();
        keyboardNavigation.current = true;
      }
    },
    [content.length, isRtl]
  );

  const widthCapStyle = styles["cap" + cap.replace("/", "")];
  return (
    <div className={classNames(styles.root, styles[layout], className)}>
      {useTabButtons && (
        <div
          className={classNames(styles.header, widthCapStyle)}
          role="tablist"
          aria-orientation="horizontal"
          onKeyDown={onKeyDown}
        >
          {content.map((tab, index) => (
            <TabHeader
              key={index}
              ariaId={_key}
              title={tab.title}
              index={index}
              active={index === active}
              onActiveChange={setActive}
              queryParameter={queryParameter}
              queryParameterValue={tab.queryParameterValue}
              layout={layout}
              focusElement={index === active && keyboardNavigation.current}
            />
          ))}
        </div>
      )}
      <div>
        {content.map((tab, index) => (
          <TabContent
            key={index}
            index={index}
            ariaId={_key}
            isAccessible={useTabButtons}
            content={tab.content}
            active={index === active}
            layout={layout}
            contentIsRendered={tab.contentIsRendered}
            customEmbeds={customEmbeds}
            cap={cap}
          />
        ))}
      </div>
    </div>
  );
};

interface TabHeaderProps {
  ariaId: string;
  active: boolean;
  index: number;
  title: ReactNode;
  onActiveChange: (index: number) => void;
  queryParameter: string | undefined;
  queryParameterValue: string | undefined;
  layout: SanityTabsLayout;
  focusElement: boolean;
}

const TabHeader = ({
  ariaId,
  title,
  active,
  onActiveChange,
  index,
  queryParameter,
  queryParameterValue,
  layout,
  focusElement
}: TabHeaderProps) => {
  const { search } = useLocation();
  const handleActiveChange = useCallback(() => {
    if (queryParameter && queryParameterValue) {
      const queryParams = new URLSearchParams(search);
      queryParams.set(queryParameter, queryParameterValue);
      navigateWithUrlSearchParams(queryParams);
    } else {
      onActiveChange(index);
    }
  }, [queryParameter, queryParameterValue, search, onActiveChange, index]);
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (focusElement) {
      ref.current?.focus();
    }
  });
  return (
    <PlainButton
      onClick={handleActiveChange}
      className={classNames(active ? styles.active : styles.inactive)}
      role="tab"
      id={`${ariaId}-tab-${index}`}
      tabIndex={active ? 0 : -1}
      aria-selected={active}
      aria-controls={`${ariaId}-tabpanel-${index}`}
      ref={ref}
    >
      <Text variant={layout === "landingPage" ? "h2" : "default"}>{title}</Text>
    </PlainButton>
  );
};

interface TabContentProps {
  ariaId: string;
  index: number;
  content: SanityRawContent;
  active: boolean;
  layout: SanityTabsLayout;
  contentIsRendered?: boolean;
  isAccessible: boolean;
  customEmbeds?: CustomEmbeds;
  cap?: Cap;
}

const TabContent = ({
  index,
  ariaId,
  active,
  content,
  layout,
  contentIsRendered,
  isAccessible,
  customEmbeds,
  cap
}: TabContentProps) => {
  const classnames = classNames(
    styles.content,
    active ? styles.active : styles.inactive
  );
  const wrappedContent = contentIsRendered ? (
    content
  ) : (
    <BlockContent
      content={content}
      customEmbeds={customEmbeds}
      cap={cap}
      className={styles.headingMargins}
    />
  );
  const attributes = isAccessible
    ? {
        id: `${ariaId}-tabpanel-${index}`,
        tabIndex: 0,
        role: "tabpanel",
        "aria-labelledby": `${ariaId}-tab-${index}`
      }
    : {};

  switch (layout) {
    case "landingPage":
      return (
        <Card type="main" className={classnames} {...attributes}>
          {wrappedContent}
        </Card>
      );
    case "genericContent":
      return (
        <div className={classnames} {...attributes}>
          {wrappedContent}
        </div>
      );
    default:
      return null;
  }
};

export default Tabs;
